import { Box } from "@mui/material";
import { useMemo } from "react";
import { decodeHtml } from "../../utils/helper";


const HtmlContent = ({ html = "" }) => {
    const content = useMemo(() => decodeHtml(html), [html]);

    if (!content) return null;

    return (
        <Box
            className="html-content"
            sx={{
                fontSize: 16,
                lineHeight: 1.75,
                color: "text.primary",
                wordWrap: "break-word",

                // Clear floats
                "&::after": {
                    content: '""',
                    display: "table",
                    clear: "both",
                },

                // Paragraphs
                "& p": {
                    margin: "0 0 16px",
                },

                // Headings
                "& h1": {
                    fontSize: { xs: "1.75rem", md: "2rem" },
                    fontWeight: 700,
                    margin: "28px 0 12px",
                    lineHeight: 1.3,
                },
                "& h2": {
                    fontSize: { xs: "1.5rem", md: "1.75rem" },
                    fontWeight: 700,
                    margin: "28px 0 12px",
                    lineHeight: 1.3,
                },
                "& h3": {
                    fontSize: { xs: "1.25rem", md: "1.5rem" },
                    fontWeight: 600,
                    margin: "24px 0 12px",
                    lineHeight: 1.3,
                },
                "& h4": {
                    fontSize: { xs: "1.1rem", md: "1.25rem" },
                    fontWeight: 600,
                    margin: "20px 0 10px",
                    lineHeight: 1.3,
                },

                // Links
                "& a:not(.editor-button-link)": {
                    color: "primary.main",
                    textDecoration: "underline",
                    wordBreak: "break-word",
                },

                // Purple CTA button
                "& a.editor-button-link": {
                    display: "inline-block",
                    padding: "10px 22px",
                    backgroundColor: "#7922D8",
                    color: "#ffffff !important",
                    borderRadius: "6px",
                    textDecoration: "none",
                    fontWeight: 600,
                    margin: "12px 0",
                    "&:hover": {
                        backgroundColor: "#6518b8",
                    },
                },

                // Lists
                "& ul": {
                    margin: "12px 0 16px",
                    paddingLeft: "28px",
                    listStyleType: "disc",
                },
                "& ol": {
                    margin: "12px 0 16px",
                    paddingLeft: "28px",
                    listStyleType: "decimal",
                },
                "& li": {
                    marginBottom: "6px",
                },

                // Blockquote
                "& blockquote": {
                    borderLeft: "4px solid",
                    borderColor: "primary.main",
                    margin: "20px 0",
                    padding: "10px 18px",
                    backgroundColor: "#f9f5ff",
                    color: "text.secondary",
                    borderRadius: "4px",
                },

                // Tables
                "& table": {
                    width: "100%",
                    borderCollapse: "collapse",
                    margin: "20px 0",
                    display: "block",
                    overflowX: "auto",
                },
                "& th": {
                    border: "1px solid",
                    borderColor: "divider",
                    padding: "10px 12px",
                    backgroundColor: "grey.100",
                    fontWeight: 600,
                    textAlign: "left",
                    whiteSpace: "nowrap",
                },
                "& td": {
                    border: "1px solid",
                    borderColor: "divider",
                    padding: "10px 12px",
                    textAlign: "left",
                },

                // Code block
                "& pre": {
                    backgroundColor: "#1e1e1e",
                    color: "#d4d4d4",
                    padding: "16px",
                    borderRadius: "8px",
                    overflowX: "auto",
                    margin: "20px 0",
                    fontSize: 14,
                },
                "& pre code": {
                    fontFamily: "Consolas, Monaco, monospace",
                    whiteSpace: "pre",
                    fontSize: 14,
                },
                "& code": {
                    fontFamily: "Consolas, Monaco, monospace",
                    fontSize: "0.9em",
                },

                // Inline styles from editor boxes
                "& .info-box": {
                    padding: "14px 18px",
                    backgroundColor: "#e8f4fd",
                    borderLeft: "4px solid #2196f3",
                    borderRadius: "6px",
                    margin: "20px 0",
                },
                "& .warning-box": {
                    padding: "14px 18px",
                    backgroundColor: "#fff8e1",
                    borderLeft: "4px solid #ff9800",
                    borderRadius: "6px",
                    margin: "20px 0",
                },
                "& .marker": {
                    backgroundColor: "#fff59d",
                    padding: "0 4px",
                },
                "& .text-small": {
                    fontSize: "0.875em",
                    color: "text.secondary",
                },

                // Images — CKEditor figure
                "& figure.image": {
                    margin: "24px 0",
                    display: "table",
                    clear: "both",
                    maxWidth: "100%",
                },
                "& figure.image img": {
                    display: "block",
                    maxWidth: "100% !important",
                    width: "100% !important",
                    height: "auto !important",
                    borderRadius: "8px",
                },

                // Side image (50% float)
                "& figure.image.image-style-side": {
                    float: { xs: "none", md: "right" },
                    maxWidth: { xs: "100%", md: "50%" },
                    margin: { xs: "16px 0", md: "0 0 16px 24px" },
                },
                "& figure.image.image-style-side img": {
                    width: "100% !important",
                },

                // Resized images
                "& figure.image.image_resized": {
                    maxWidth: "100%",
                },
                "& figure.image.image_resized img": {
                    width: "100% !important",
                    height: "auto !important",
                },

                // Block / center images
                "& figure.image.image-style-block": {
                    float: "none",
                    marginLeft: "auto",
                    marginRight: "auto",
                    textAlign: "center",
                    maxWidth: "100%",
                },

                // Inline image style
                "& figure.image.image-style-inline": {
                    display: "inline-block",
                    margin: "0 8px",
                    maxWidth: "100%",
                },

                // Standalone img (agar figure ke bahar ho)
                "& img": {
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: "8px",
                },

                // YouTube / media embed
                "& .media": {
                    position: "relative",
                    paddingBottom: "56.25%",
                    height: 0,
                    overflow: "hidden",
                    margin: "20px 0",
                    borderRadius: "8px",
                },
                "& .media iframe": {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    border: 0,
                    borderRadius: "8px",
                },

                // Horizontal rule
                "& hr": {
                    border: "none",
                    borderTop: "1px solid",
                    borderColor: "divider",
                    margin: "24px 0",
                },

                // Todo list checkboxes (agar editor se aaye)
                "& ul[data-checklist]": {
                    listStyle: "none",
                    paddingLeft: 0,
                },
            }}
            dangerouslySetInnerHTML={{ __html: content }}
        />
    );
};

export default HtmlContent;